import { FC, useEffect, useState } from "react"
import Breadcrumb from "../../../components/common/Breadcrumbs";
import { useFindUserSettingsByGroupNameQuery } from "../../../components/common/userSetting/userSettingsApi";
import { formatValueAsDate } from "../../../helpers/utils/DateUtils";
import { getBooleanDisplayIcon, getBooleanWithDisplayIcon } from "../../../helpers/utils/Utils";
import { DataTable } from "../../../components/common/datatable/DataTable";
import { createClickableLink, SearchSelectWithIndeterminateBoolean } from "../../../components/common/datatable/DataTableUtils";
import { SearchInputWrapper } from "../../../components/common/datatable/filter/SearchInputWrapper";
import { useLazyExportUserDataQuery, useSearchUsersQuery, useUserLockedUserServicesMutation } from "./usersApi";
import IconButton from "../../../components/common/form/IconButton";
import { FaPlus } from "react-icons/fa6";
import { useLocation, useNavigate } from "react-router-dom";
import { DataTableColumn } from "../../../components/common/datatable/DataTableTypes";
import ConfirmationModal from "../../../components/layout/modal/ConfirmationModal";
import { SearchSelectWithList } from "../../../components/common/datatable/filter/SearchSelectWrapper";
import { useFindAllRolesQuery } from "../roles/rolesApi";
import { useGetAllCustomersListQuery } from "../../customer/customerApi";
import { SearchDatePickerWrapper } from "../../../components/common/datatable/filter/SearchDatePickerWrapper";


const Users: FC = () => {

    const navigate = useNavigate()

    const [crumbs] = useState([
        { content: "Home", url: "/home" },
        { content: "Users", url: "" },
    ])

    useEffect(() => {
        document.title = "Users";
    });

    const { data: userSettings } = useFindUserSettingsByGroupNameQuery("Users");
    const [triggerExportUserDataQuery] = useLazyExportUserDataQuery();
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [userLocked, userLockedApiResp] = useUserLockedUserServicesMutation();
    const [userObj, setUserObj] = useState("");
    const roleData = useFindAllRolesQuery("");
    const customerData = useGetAllCustomersListQuery("");
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);

    const defaultFilter = {
        ...(searchParams.has("customerNumber") && {
            customerNumber: searchParams.get("customerNumber")
        })
    };
    const handleUpdate = (e: any) => {
        e.preventDefault();
        handleRTK();
    };

    const handleRTK = async () => {
        try {
            await userLocked(userObj); // API call
            setShowConfirmation(false); // After successful response
        } catch (error) {
            console.error("Failed to update user lock status:", error);
            // Optional: you can also show an error toast or message here
        }
    };

    const columns: DataTableColumn[] = [

        {
            label: "Name",
            accessor: "username",
            visibility: { canColumnShow: true, showByDefault: true },
            cell: (props: any) => {
                let path = `/users/${props.row.original.id}`;
                return createClickableLink(path, props.value, userSettings, false, "", ["USR-R"]);
            },
            filterOptions: [{
                filterComponent: <SearchInputWrapper
                    accessor={'username'}
                    placeholder={'Enter Name...'}
                    name={'username'} />
            }]
        },
        {
            label: "Email",
            accessor: "email",
            filterOptions: [{
                filterComponent: <SearchInputWrapper
                    accessor={'email'}
                    placeholder={'Enter Email...'}
                    name={'email'} />
            }]
        },
        {
            label: "Customer",
            accessor: "customer.customerName",
            cell: (props: any) => {
                return <>
                    <span>{props.row.original.customer.customerName} ({props.row.original.customer.customerNumber})</span>
                </>
            },
            filterOptions: [{
                filterComponent: SearchSelectWithList(customerData?.data,
                    (customer: any) => `${customer.customerName} (${customer.customerNumber})`,
                    "customerNumber", "Select Customer...", false, "customerNumber"),
                overrideFilterAccessor: "customerNumber"
            }]

        },
        {
            label: "Roles",
            accessor: "roles",
            canSort: false,
            cell: (props: any) => {
                return props.row.original.roles?.join(', ');
            },
            filterOptions: [{
                filterComponent: roleData && SearchSelectWithList(roleData?.data, "name", "name", "Select Role(s)...", false, "roles"),
            }]
        },
        {
            label: "Created Date",
            accessor: "creationDate",
            cell: (props: any) => {
                return formatValueAsDate(props.row.original.creationDate);
            },
            filterOptions: [
                {
                    filterComponent: <SearchDatePickerWrapper accessor={"createdDateFrom"} />,
                    overrideFilterAccessor: "createdDateFrom",
                    overrideFilterLabel: "Created Date - From",
                },
                {
                    filterComponent: <SearchDatePickerWrapper accessor={"createdDateFrom"} />,
                    overrideFilterAccessor: "createdDateTo",
                    overrideFilterLabel: "Created Date - To",
                }],
        },

        {
            label: "Unlocked",
            accessor: "accountNonLocked",
            cell: (props: any) => (
                <div
                    onClick={() => {
                        if(!props.row.original.accountNonLocked) {
                            setShowConfirmation(true);
                            setUserObj(props.row.original.id);
                        }
                    }}
                    style={{ cursor: "pointer" }}
                >
                    {getBooleanDisplayIcon(props.row.original.accountNonLocked, "Enabled", "Disabled")}
                </div>
            ),
            filterOptions: [{
                filterComponent: SearchSelectWithIndeterminateBoolean("Select Lock Status...", "accountNonLocked", "Unlocked", "Locked"),
            }]
        },

        {
            label: "In Training", accessor: "inTraining",
            cell: (props: any) => getBooleanWithDisplayIcon(props.row.original.inTranning, "Training", "Not Training", props.row.original.customer?.customerLevel),
            filterOptions: [{
                filterComponent: SearchSelectWithIndeterminateBoolean("Select training status...", "inTraining", "Training", "Not Training"),
            }],
            overrideSortColumn: 'inTraining'
        },
        {
            label: "Status", accessor: "enabled",
            cell: (props: any) => getBooleanDisplayIcon(props.row.original.enabled, "Enabled", "Disabled"),
            filterOptions: [{
                filterComponent: SearchSelectWithIndeterminateBoolean("Select status...", "status", "Enabled", "Disabled"),
                overrideFilterLabel: "Enabled",
            }]
        },
    ]

    return (

        <div className="user-container px-5 py-5">
            <div className="flex justify-between">
                <Breadcrumb crumbs={crumbs} />
            </div>

            <DataTable
                columns={columns}
                actions={{
                    fetchAction: useSearchUsersQuery,
                    exportAction: triggerExportUserDataQuery
                }}
                userSettingGroup={"Users"}
                defaultSortOptions={{
                    accessor: "creationDate",
                    direction: "desc"
                }}
                defaultFilter={{
                    ...defaultFilter
                }}
                buttonOptions={{
                    addButton:
                    {
                        button: <IconButton
                            icon={<FaPlus />}
                            hasBackground={true}
                            onClick={() => {
                                navigate("/users/create")
                            }}
                            title={"Add User"}
                        />,
                        permissions: ["USR-C"]
                    },
                }}
                basePermission={"USR"}
            />
            {
                showConfirmation &&
                <ConfirmationModal title={"Update User"}
                    bodyMessage={"Are you sure you want to unlock this user?"}
                    onSubmit={handleUpdate} show={showConfirmation}
                    setShow={setShowConfirmation}
                    isLoading={userLockedApiResp.isLoading}
                />
            }
        </div>
    )
}
export default Users